(function () {
  'use strict';

  /* ============================================================
     Module 1 — Nav dropdown / mega-menu ARIA + arrow-key support
     ============================================================ */
  function initNavDropdowns() {
    function closeEntry(entry) {
      if (entry.isMega) entry.container.classList.remove('is-active');
      else entry.item.classList.remove('show-dropdown');
      entry.link.setAttribute('aria-expanded', 'false');
    }

    function panelItems(entry) {
      return Array.prototype.slice.call(entry.panel.querySelectorAll('a'));
    }

    function handleTriggerKeydown(event, entry, allEntries) {
      var key = event.key;
      var index = allEntries.indexOf(entry);

      if (key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        entry.open();
        var items = panelItems(entry);
        if (items.length) (key === 'ArrowDown' ? items[0] : items[items.length - 1]).focus();
      } else if (key === 'ArrowRight' || key === 'ArrowLeft') {
        event.preventDefault();
        var wasOpen = entry.link.getAttribute('aria-expanded') === 'true';
        var nextIndex = key === 'ArrowRight'
          ? (index + 1) % allEntries.length
          : (index - 1 + allEntries.length) % allEntries.length;
        var next = allEntries[nextIndex];
        entry.close();
        if (wasOpen) next.open();
        next.link.focus();
      } else if (key === 'Escape') {
        entry.close();
        entry.link.focus();
      }
    }

    function handlePanelKeydown(event, entry, allEntries) {
      var key = event.key;
      var items = panelItems(entry);
      var currentIndex = items.indexOf(document.activeElement);

      if (key === 'ArrowDown' || key === 'ArrowUp') {
        if (currentIndex === -1) return;
        event.preventDefault();
        var nextIndex = key === 'ArrowDown'
          ? (currentIndex + 1) % items.length
          : (currentIndex - 1 + items.length) % items.length;
        items[nextIndex].focus();
      } else if (key === 'ArrowRight' || key === 'ArrowLeft') {
        event.preventDefault();
        var index = allEntries.indexOf(entry);
        var nextIndex = key === 'ArrowRight'
          ? (index + 1) % allEntries.length
          : (index - 1 + allEntries.length) % allEntries.length;
        var next = allEntries[nextIndex];
        entry.close();
        next.open();
        var nextItems = panelItems(next);
        (nextItems[0] || next.link).focus();
      } else if (key === 'Escape') {
        entry.close();
        entry.link.focus();
      }
    }

    function setup(item, index, allEntries) {
      var link = item.querySelector(':scope > label > a');
      var isMega = item.classList.contains('has-mega-menu');
      var container = isMega
        ? item.querySelector(':scope > section.mega-menu__section')
        : item;
      var panel = isMega
        ? (container && container.querySelector('.mega-menu'))
        : item.querySelector(':scope > .navbar-dropdown');

      if (!link || !container || !panel) return null;
      if (item.__a11yNavEntry) return item.__a11yNavEntry; // already set up
      if (!panel.id) panel.id = 'nav-flyout-' + (++flyoutCounter);

      link.setAttribute('aria-haspopup', 'true');
      link.setAttribute('aria-expanded', 'false');
      link.setAttribute('aria-controls', panel.id);
      item.removeAttribute('aria-expanded');
      item.removeAttribute('aria-haspopup');

      var entry = { item: item, link: link, container: container, panel: panel, isMega: isMega };

      entry.open = function () {
        allEntries.forEach(function (other) {
          if (other !== entry) closeEntry(other);
        });
        if (isMega) container.classList.add('is-active');
        else item.classList.add('show-dropdown');
        link.setAttribute('aria-expanded', 'true');
      };
      entry.close = function () { closeEntry(entry); };

      function maybeClose() {
        window.requestAnimationFrame(function () {
          var stillHovered = item.matches(':hover');
          var stillFocused = item.contains(document.activeElement);
          if (!stillHovered && !stillFocused) entry.close();
        });
      }

      item.addEventListener('mouseenter', entry.open);
      item.addEventListener('mouseleave', maybeClose);
      item.addEventListener('focusin', entry.open);
      item.addEventListener('focusout', maybeClose);
      link.addEventListener('keydown', function (event) {
        handleTriggerKeydown(event, entry, allEntries);
      });
      panel.addEventListener('keydown', function (event) {
        handlePanelKeydown(event, entry, allEntries);
      });

      item.__a11yNavEntry = entry;
      return entry;
    }

    var flyoutCounter = 0;

    // Brands and Collections are mega menus. The theme renders them as
    // separate sections and moves them into the nav AFTER the page loads, so
    // they didn't exist yet when this first ran and never got set up. Scan
    // again whenever the header changes; items already set up are skipped.
    function scanMenus() {
      document.querySelectorAll('.header__menu-items').forEach(function (menu) {
        var entries = menu.__a11yNavEntries || (menu.__a11yNavEntries = []);
        var wrappers = Array.prototype.slice.call(
          menu.querySelectorAll(':scope > .navbar-item.has-dropdown, :scope > .navbar-item.has-mega-menu')
        );
        var ordered = [];
        wrappers.forEach(function (item, index) {
          var entry = setup(item, index, entries);
          if (entry) ordered.push(entry);
        });
        // Keep the shared list in on-screen order so arrow keys move correctly.
        entries.length = 0;
        Array.prototype.push.apply(entries, ordered);
      });
    }

    scanMenus();

    var scanQueued = false;
    new MutationObserver(function () {
      if (scanQueued) return;
      scanQueued = true;
      window.requestAnimationFrame(function () {
        scanQueued = false;
        scanMenus();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ============================================================
     Module 2 — Cart drawer focus management + status announcement
     ============================================================ */
  function initCartAnnouncements() {
    var STATUS_ID = 'a11y-cart-status';
    var lastTrigger = null;
    var lastAddedTitle = null;
    var cartWatcher = null;
    var pending = null; // the add-to-cart currently waiting to be announced
    var cartIsOpen = false;
    var lastActivation = { time: 0, target: null };

    function removeStatusRegion() {
      var region = document.getElementById(STATUS_ID);
      if (region && region.parentNode) region.parentNode.removeChild(region);
    }

    function announce(message) {
      // role="alert" is only reliably re-announced by AT on a fresh node,
      // so the region is torn down and recreated each time the modal opens
      // rather than reusing one persistent element.
      removeStatusRegion();
      var region = document.createElement('div');
      region.id = STATUS_ID;
      region.setAttribute('role', 'alert');
      region.setAttribute('aria-atomic', 'true');
      region.style.cssText =
        'position:absolute;width:1px;height:1px;padding:0;margin:-1px;' +
        'overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
      document.body.appendChild(region);
      window.setTimeout(function () { region.textContent = message; }, 50);
    }

    function setupPanel(panel) {
      if (panel.dataset.a11yCartInit) return;
      panel.dataset.a11yCartInit = 'true';

      var title = panel.querySelector('.ajax-cart__cart-title');
      if (title) {
        if (!title.id) title.id = 'ajax-cart-title-' + Math.random().toString(36).slice(2);
        panel.setAttribute('aria-labelledby', title.id);
      } else {
        panel.setAttribute('aria-label', 'Shopping cart');
      }
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');

      panel.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          closePanel(panel);
        } else if (event.key === 'Tab') {
          trapFocus(event, panel);
        }
      });
    }

    function trapFocus(event, panel) {
      var focusable = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function closePanel(panel) {
      var closeButton = panel.querySelector('[data-ajax-cart-close]');
      if (closeButton) closeButton.click();
      removeStatusRegion();
      if (lastTrigger) lastTrigger.focus();
    }

    function isRendered(el) {
      if (!el) return false;
      var rect = el.getBoundingClientRect();
      // Off-canvas drawers keep width/height and visibility:visible while
      // closed (just translated off screen), so a rect-length/visibility
      // check alone reports them as "open" before they actually are —
      // that false positive is what made focus land before the drawer
      // really opened on the first add-to-cart and only "catch up" by the
      // second. Require the rect to actually intersect the viewport too.
      if (!rect.width || !rect.height) return false;
      if (rect.right <= 0 || rect.left >= window.innerWidth ||
          rect.bottom <= 0 || rect.top >= window.innerHeight) return false;
      var style = window.getComputedStyle(el);
      if (style.visibility === 'hidden' || style.display === 'none') return false;
      if (parseFloat(style.opacity) === 0) return false;
      return true;
    }

    function findVisiblePanel() {
      var panels = document.querySelectorAll('[data-ajax-cart-mini_cart], [data-ajax-cart-drawer]');
      for (var i = 0; i <panels.length; i++) {
        if (isRendered(panels[i])) return panels[i];
      }
      return null;
    }

    function cleanText(text) {
      return (text || '').replace(/\s+/g, ' ').trim();
    }

    // Reads the product title from the product form the shopper clicked.
    // product__form.liquid puts the full product JSON in data-product.
    function getProductTitleFromButton(button) {
      var form = button.closest('[data-product]');
      if (!form) return null;
      try {
        var product = JSON.parse(form.getAttribute('data-product'));
        return product && product.title ? cleanText(product.title) : null;
      } catch (e) {
        return null;
      }
    }

    function findLatestItemName(panel) {
      var titles = panel.querySelectorAll('.ajax-cart__product-title');
      if (!titles.length) return null;

      // Best case: find the cart line that matches the product just added.
      if (lastAddedTitle) {
        var wanted = lastAddedTitle.toLowerCase();
        for (var i = 0; i < titles.length; i++) {
          var name = cleanText(titles[i].textContent);
          if (name.toLowerCase().indexOf(wanted) !== -1) return name;
        }
      }

      // Fallback: Shopify lists the newest item first, so use the top one.
      return cleanText(titles[0].textContent);
    }

    // Typed-out sizes for the announcement, matching the size-label snippet:
    // L -> Large, XL -> Extra-Large, REG -> Regular, X-LONG -> Extra-Long,
    // PORT-S/R/L -> Portly-Short/Regular/Long, LMT -> Large Mid-Tall,
    // 2XMT -> 2X Mid-Tall, LT -> Large-Tall, 2XLT -> 2X-Tall, 2XB -> 2X-Big.
    // Inch marks -> "inches".
    var EXACT_SIZES = {
      L: 'Large', XL: 'Extra-Large', REG: 'Regular', LONG: 'Long', XLONG: 'Extra-Long',
      PORTS: 'Portly-Short', PORTR: 'Portly-Regular', PORTL: 'Portly-Long'
    };

    function spokenOption(text) {
      var raw = cleanText(text);
      var code = raw.toUpperCase().replace(/[\s-]/g, '');
      if (EXACT_SIZES[code]) return EXACT_SIZES[code];

      var mid = /^(L|XL|[1-9]X)MT$/.exec(code);
      if (mid) {
        var midBase = mid[1] === 'L' ? 'Large' : mid[1] === 'XL' ? 'Extra-Large' : mid[1];
        return midBase + ' Mid-Tall';
      }

      var match = code.length >= 2 && /^([1-9]X|XL|X|L|M)?L?(T|B)$/.exec(code);
      if (match) {
        var type = match[2] === 'T' ? 'Tall' : 'Big';
        var prefix = match[1];
        var base;
        if (!prefix || prefix === 'L') base = 'Large';
        else if (prefix === 'M') base = 'Medium';
        else if (prefix === 'X') base = type === 'Big' ? '1X' : 'XL';
        else base = prefix;
        return base + '-' + type;
      }
      return raw.replace(/(\d)\s*(″|''|")/g, '$1 inches');
    }

    function nameFromItem(item) {
      if (!item) return null;
      var name = cleanText(item.product_title || item.title);
      if (!name) return null;
      var options = item.options_with_values
        ? item.options_with_values.map(function (o) { return o.value; })
        : (item.variant_title ? item.variant_title.split(' / ') : []);
      options = options.filter(function (o) { return o && o !== 'Default Title'; });
      if (options.length) name += ', ' + options.map(spokenOption).join(', ');
      return name;
    }

    function announceAndFocus(panel, item, cartCount) {
      setupPanel(panel);

      var itemCount = panel.querySelector('[data-bind="itemCount"]');
      var count = cartCount != null
        ? String(cartCount)
        : (itemCount ? itemCount.textContent.trim() : null);
      var itemName = nameFromItem(item) || findLatestItemName(panel);

      var message = 'Shopping cart opened. ' +
        (itemName ? itemName + ' added to your cart.' : 'Item added to your cart.');
      if (count) {
        message += ' Cart now has ' + count + (count === '1' ? ' item.' : ' items.');
      }
      announce(message);
      panel.focus();
    }

    function stopWatching() {
      if (cartWatcher) {
        cartWatcher.observer.disconnect();
        window.clearTimeout(cartWatcher.timeoutId);
        cartWatcher = null;
      }
    }

    function waitForCartToOpen() {
      stopWatching();

      var panel = findVisiblePanel();
      if (panel) {
        panelReady(panel);
        return;
      }

      // A fixed-attempt setTimeout poll (the previous approach) times out
      // before the drawer appears whenever the add-to-cart request is slow
      // (e.g. a cold first request), silently dropping the focus/announce
      // step. Watching the DOM directly reacts the instant the drawer
      // actually renders, no matter how long the request takes.
      var observer = new MutationObserver(function () {
        var found = findVisiblePanel();
        if (found) {
          stopWatching();
          panelReady(found);
        }
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'aria-hidden']
      });
      var timeoutId = window.setTimeout(stopWatching, 8000);
      cartWatcher = { observer: observer, timeoutId: timeoutId };
    }

    /* ---- Wait for BOTH the drawer and Shopify's add-to-cart response ----
       The drawer can already be open (or open before it refreshes) when a
       second size is added, so reading the drawer's contents announced the
       previous item. Shopify's /cart/add response says exactly which
       product and variant was just added, so that's what gets announced. */
    function clearPending() {
      if (pending) window.clearTimeout(pending.fallbackId);
      pending = null;
    }

    function finishPending() {
      if (!pending || !pending.panel) return;
      var p = pending;
      clearPending();
      announceAndFocus(p.panel, p.item, p.count);
    }

    function panelReady(panel) {
      if (!pending) return;
      pending.panel = panel;
      if (pending.responded) {
        finishPending();
      } else {
        // Response not seen yet; don't wait forever for it.
        window.clearTimeout(pending.fallbackId);
        pending.fallbackId = window.setTimeout(finishPending, 2500);
      }
    }

    function handleAddResponse(ok, data) {
      if (!pending) return;
      if (!ok) {
        // e.g. sold out / quantity limit: nothing was added, so don't announce.
        stopWatching();
        clearPending();
        return;
      }
      pending.item = data && data.items ? data.items[0] : data;
      var p = pending;
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (res) { return res.json(); })
        .then(function (cart) { p.count = cart.item_count; })
        .catch(function () {})
        .then(function () {
          if (pending !== p) return;
          p.responded = true;
          if (p.panel) finishPending();
        });
    }

    function isAddUrl(url) {
      return /\/cart\/add(\.js|\.json)?(\?|$)/.test(String(url || ''));
    }

    if (!window.__wbtCartAddHooked) {
      window.__wbtCartAddHooked = true;

      var xhrOpen = XMLHttpRequest.prototype.open;
      var xhrSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__wbtIsCartAdd = isAddUrl(url);
        return xhrOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function () {
        if (this.__wbtIsCartAdd) {
          this.addEventListener('loadend', function () {
            var ok = this.status >= 200 && this.status < 300;
            var data = null;
            try {
              data = this.responseType === 'json' ? this.response : JSON.parse(this.responseText);
            } catch (e) {}
            handleAddResponse(ok, data);
          });
        }
        return xhrSend.apply(this, arguments);
      };

      if (window.fetch) {
        var originalFetch = window.fetch;
        window.fetch = function (input) {
          var url = typeof input === 'string' ? input : (input && input.url);
          var request = originalFetch.apply(this, arguments);
          if (isAddUrl(url)) {
            request.then(function (res) {
              return res.clone().json().then(
                function (data) { handleAddResponse(res.ok, data); },
                function () { handleAddResponse(res.ok, null); }
              );
            }, function () { handleAddResponse(false, null); });
          }
          return request;
        };
      }
    }

    /* ---- Cart opened some other way (e.g. the cart icon) ----
       Announce "Shopping cart opened" and move focus into it. Only when the
       shopper clicked or pressed Enter/Space on the cart button just before,
       so a mini cart that opens on mouse hover doesn't steal focus. */
    function countSentence(count) {
      if (count == null || count === '') return '';
      return ' Cart has ' + count + (String(count) === '1' ? ' item.' : ' items.');
    }

    function announceCartOpened(panel) {
      setupPanel(panel);
      if (lastActivation.target && !panel.contains(lastActivation.target)) {
        lastTrigger = lastActivation.target;
      }
      panel.focus();
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (res) { return res.json(); })
        .then(function (cart) { return cart.item_count; })
        .catch(function () {
          var el = panel.querySelector('[data-bind="itemCount"]');
          return el ? el.textContent.trim() : null;
        })
        .then(function (count) {
          if (cartIsOpen) announce('Shopping cart opened.' + countSentence(count));
        });
    }

    var stateCheckQueued = false;
    function checkCartState() {
      stateCheckQueued = false;
      var panel = findVisiblePanel();
      var open = !!panel;
      if (open === cartIsOpen) return;
      cartIsOpen = open;

      if (!open) {
        removeStatusRegion();
        return;
      }
      if (pending) return; // the add-to-cart flow announces this one
      var fromCartButton = lastActivation.target && lastActivation.target.closest &&
        lastActivation.target.closest('[data-ajax-cart-trigger], [data-ajax-cart-open], .header-cart');
      if (fromCartButton && Date.now() - lastActivation.time < 1500) announceCartOpened(panel);
    }

    new MutationObserver(function () {
      if (stateCheckQueued) return;
      stateCheckQueued = true;
      window.requestAnimationFrame(checkCartState);
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden']
    });
    // Drawers that slide in with CSS transitions finish after the last mutation.
    document.addEventListener('transitionend', function () {
      if (!stateCheckQueued) { stateCheckQueued = true; window.requestAnimationFrame(checkCartState); }
    }, true);

    function noteActivation(event) {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      lastActivation = { time: Date.now(), target: event.target.closest ? (event.target.closest('a, button, [tabindex]') || event.target) : event.target };
    }
    document.addEventListener('click', noteActivation, true);
    document.addEventListener('keydown', noteActivation, true);

    document.querySelectorAll('[data-ajax-cart-mini_cart], [data-ajax-cart-drawer]')
      .forEach(setupPanel);

    document.addEventListener('click', function (event) {
      var addButton = event.target.closest('.ajax-submit');
      if (addButton) {
        lastTrigger = addButton;
        lastAddedTitle = getProductTitleFromButton(addButton);
        clearPending();
        pending = { panel: null, item: null, count: null, responded: false, fallbackId: null };
        waitForCartToOpen();
        return;
      }
      var closeButton = event.target.closest('[data-ajax-cart-close]');
      if (closeButton) {
        removeStatusRegion();
        if (lastTrigger) lastTrigger.focus();
      }
    });
  }

  /* ============================================================
     Module 3 — Hide zoom overlay images from screen readers
     ============================================================ */
  // The product zoom adds a second copy of each gallery image (.zoomImg)
  // with the same generic alt text on every image. The real image right
  // next to it already has a descriptive alt, so the copy is decorative.
  function initZoomImageCleanup() {
    function clean(root) {
      var imgs = root.querySelectorAll ? root.querySelectorAll('img.zoomImg') : [];
      for (var i = 0; i < imgs.length; i++) {
        if (imgs[i].getAttribute('alt') !== '') imgs[i].setAttribute('alt', '');
        if (imgs[i].getAttribute('aria-hidden') !== 'true') imgs[i].setAttribute('aria-hidden', 'true');
      }
    }

    clean(document);
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'attributes') {
          if (m.target.classList && m.target.classList.contains('zoomImg')) clean(m.target.parentNode);
        } else {
          for (var j = 0; j < m.addedNodes.length; j++) {
            var node = m.addedNodes[j];
            if (node.nodeType !== 1) continue;
            if (node.classList.contains('zoomImg')) clean(node.parentNode);
            else clean(node);
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['alt'] });
  }

  /* ============================================================
     Bootstrap — run all modules once the DOM is ready
     ============================================================ */
  function init() {
    initNavDropdowns();
    initCartAnnouncements();
    initZoomImageCleanup();
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();
})();